import { formatCopenhagenDateTime } from '@/lib/clinicDateTime';

export type MessageSenderKind = 'patient' | 'clinic';

/** Display role for a message sender. Mapped from stored sender kinds. */
export type MessageSenderRole = 'patient' | 'practitioner';

export interface MessageSenderFields {
  sender: MessageSenderKind | string;
  sender_name?: string | null;
}

export interface MessageSenderContext {
  patientFirstName?: string | null;
  patientLastName?: string | null;
  patientName?: string | null;
}

export interface ResolvedMessageSender {
  name: string;
  role: MessageSenderRole;
}

const ROLE_BY_SENDER: Record<string, MessageSenderRole> = {
  patient: 'patient',
  clinic: 'practitioner',
};

export function personDisplayName(
  ...parts: Array<string | null | undefined>
): string {
  return parts
    .map((part) => part?.replace(/\s+/g, ' ').trim() ?? '')
    .filter(Boolean)
    .join(' ');
}

export function messageSenderRole(sender: string): MessageSenderRole {
  return ROLE_BY_SENDER[sender] ?? 'practitioner';
}

export function messageSenderRoleKey(role: string): string {
  return `messages.sender_role.${role}`;
}

export function defaultMessageSenderRoleLabel(role: string): string {
  if (role === 'patient') return 'Patient';
  if (role === 'practitioner') return 'Practitioner';
  return role;
}

/**
 * Resolve the person who sent a message plus their role.
 * Patient senders use the patient record; clinic/practitioner senders use
 * the name stored on the message (from the sending user's record).
 */
export function resolveMessageSender(
  message: MessageSenderFields,
  context: MessageSenderContext = {},
): ResolvedMessageSender {
  const storedName = message.sender_name?.replace(/\s+/g, ' ').trim() ?? '';
  const patientName =
    context.patientName?.replace(/\s+/g, ' ').trim() ||
    personDisplayName(context.patientFirstName, context.patientLastName);
  const role = messageSenderRole(message.sender);

  if (role === 'patient') {
    return {
      name: patientName || storedName,
      role,
    };
  }

  return {
    name: storedName,
    role,
  };
}

export function conversationLatestSender(conversation: {
  latest_sender: MessageSenderKind | string;
  latest_sender_name: string | null;
  patient_first_name: string;
  patient_last_name: string;
}): ResolvedMessageSender {
  return resolveMessageSender(
    {
      sender: conversation.latest_sender,
      sender_name: conversation.latest_sender_name,
    },
    {
      patientFirstName: conversation.patient_first_name,
      patientLastName: conversation.patient_last_name,
    },
  );
}

export function formatMessageDateTime(isoString: string, _language: string): string {
  return formatCopenhagenDateTime(isoString);
}
